import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, GaugeIcon } from 'lucide-react';
import { ButtonLink } from './ButtonLink';
import { heroSlides, heroVideo } from '../data/site';
const chips = [{
  label: 'Yamaha Certified Technician',
  Icon: BadgeCheckIcon
}, {
  label: 'Honda Marine Certified Tech',
  Icon: BadgeCheckIcon
}, {
  label: 'Drive-In Test Tank',
  Icon: GaugeIcon
}];
interface HeroProps {
  background: 'video' | 'slideshow';
}
export function Hero({
  background
}: HeroProps) {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const isVideo = background === 'video';
  useEffect(() => {
    if (reduce || isVideo) return;
    const t = window.setInterval(() => setActive(p => (p + 1) % heroSlides.length), 6500);
    return () => window.clearInterval(t);
  }, [reduce, isVideo]);
  return <section id="top" aria-label="Introduction" className="relative isolate flex min-h-[calc(100svh-140px)] w-full items-end overflow-hidden bg-navy-900" data-id="element-205" data-mp="381nv9anuav4h">
      {isVideo ? heroVideo.src && !reduce ? <video className="absolute inset-0 -z-20 h-full w-full object-cover" src={heroVideo.src} poster={heroVideo.poster} autoPlay muted loop playsInline preload="auto" aria-label={heroVideo.alt} data-id="element-206" data-mp="3o73q0dt77y94" /> : <img src={heroVideo.poster} alt={heroVideo.alt} className="absolute inset-0 -z-20 h-full w-full object-cover" data-id="element-207" data-mp="358rbsdnfjy6o" /> : heroSlides.map((s, i) => <motion.img key={s.src} src={s.src} alt={i === active ? s.alt : ''} aria-hidden={i !== active} initial={false} animate={{
      opacity: i === active ? 1 : 0,
      scale: i === active ? 1.04 : 1
    }} transition={{
      opacity: {
        duration: 1.6,
        ease: 'easeInOut'
      },
      scale: {
        duration: 8,
        ease: 'linear'
      }
    }} className="absolute inset-0 -z-20 h-full w-full object-cover" data-id="element-208" data-mp="2qhqtzi0smrxv" data-mpi={i} />)}
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy-950/90 via-navy-900/65 to-navy-900/10" aria-hidden="true" data-id="element-209" data-mp="najg5kfzo7df" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-navy-950/80 via-transparent to-transparent" aria-hidden="true" data-id="element-210" data-mp="ia5j0xfxehu6" />

      <div className="gutter w-full pb-14 pt-24 md:pb-20 lg:pb-24" data-id="element-211" data-mp="1b0nizplcs33d">
        <div className="grid items-end gap-10 lg:grid-cols-[minmax(0,1fr)_auto]" data-id="element-212" data-mp="1ayt6mzo5djaw">
          <div data-id="element-213" data-mp="162m0p6vr47sy">
            <motion.h1 initial={reduce ? false : {
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} className="display max-w-[22ch] text-[clamp(2.6rem,6vw,7.5rem)] font-bold text-white" data-id="element-214" data-mp="15zq69sj44199">
              Yamaha &amp; Honda Outboards. <span className="text-water-300" data-id="element-215" data-mp="3hr3qlsftw548">Sold, Repowered and Serviced</span> Right Here in Edenton.
            </motion.h1>
            <p className="mt-6 max-w-[58ch] text-lg leading-relaxed text-white/85 md:text-xl 4xl:text-2xl" data-id="element-216" data-mp="1xavzepf1qi9w">
              The area’s only authorized Yamaha <strong className="font-semibold text-white" data-id="element-217" data-mp="26a9a1lewpcw7">and</strong> Honda outboard dealer, with
              factory-certified technicians and the only hydraulic drive-in test tank in the area.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-218" data-mp="2g0c1viaakksy">
              <ButtonLink href="#repower-quote" variant="water" className="min-h-[56px] px-8 text-base" data-id="element-219" data-mp="2g257qvd2z0zw">
                Get a Repower Quote
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-220" data-mp="2p08vlybjg9uv" />
              </ButtonLink>
              <ButtonLink href="#book-service" variant="outlineLight" className="min-h-[56px] px-8 text-base" data-id="element-221" data-mp="2tztuy9umek84">
                Book Service
              </ButtonLink>
            </div>
            <ul className="mt-8 flex flex-wrap gap-2" data-id="element-222" data-mp="zl0zcatqltg6">
              {chips.map(({
              label,
              Icon
            }) => <li key={label} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-2 text-[13px] font-semibold text-white ring-1 ring-inset ring-white/25 backdrop-blur-sm" data-id="element-223" data-mp="4auo8pqfqxn9">
                  <Icon className="h-4 w-4 text-water-300" aria-hidden="true" data-id="element-224" data-mp="e7ha9huhzuqv" />
                  {label}
                </li>)}
            </ul>
          </div>
          <div className={`gap-2 lg:flex-col ${isVideo ? 'hidden' : 'flex'}`} role="group" aria-label="Choose slide" data-id="element-225" data-mp="3hr26k4vrymtw">
            {heroSlides.map((s, i) => <button key={s.src} type="button" onClick={() => setActive(i)} aria-label={`Show photo ${i + 1} of ${heroSlides.length}`} aria-current={i === active} className="group flex h-8 items-center lg:h-auto lg:w-8 lg:justify-center lg:py-2" data-id="element-226" data-mp="3rftvs59gat14" data-mpi={i}>
                <span className={`block h-1 rounded-full transition-[width,background-color] duration-300 lg:h-auto lg:w-1 lg:transition-[height,background-color] ${i === active ? 'w-10 bg-white lg:h-10' : 'w-5 bg-white/40 group-hover:bg-white/70 lg:h-5'}`} data-id="element-227" data-mp="2u0g02i1f5am0" />
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}